import { useRef, useEffect } from 'react';
import { Pencil, Trash2, Copy, List as ListWait, Archive, ArchiveRestore } from 'lucide-react';
import clsx from 'clsx';

interface SessionTableProps {
  sessions: any[];
  stages: { id: string; title: string }[];
  centers: { id: string; name: string }[];
  handleOpenModal: (session?: any) => void;
  handleDuplicate: (session: any) => void;
  setDeleteId: (id: string | null) => void;
  waitingListCounts?: Record<string, number>;
  onViewWaitingList: (activityId: string) => void;
  onArchive: (id: string) => void;
  onUnarchive: (id: string) => void;
  showArchived: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
}

const SessionTable = ({
  sessions,
  stages,
  centers,
  handleOpenModal,
  handleDuplicate,
  setDeleteId,
  waitingListCounts = {},
  onViewWaitingList,
  onArchive,
  onUnarchive,
  showArchived,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
}: SessionTableProps) => {
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const allSelected = sessions.length > 0 && sessions.every(s => selectedIds.has(s.id));
  const someSelected = sessions.some(s => selectedIds.has(s.id)) && !allSelected;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const formatArchivedAt = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (sessions.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-md p-12 text-center">
        <Archive className="h-10 w-10 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 text-sm">
          {showArchived
            ? 'Aucune session archivée pour les filtres sélectionnés.'
            : 'Aucune session active pour les filtres sélectionnés.'}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="pl-4 pr-2 py-3 w-10">
                <input
                  ref={headerCheckboxRef}
                  type="checkbox"
                  checked={allSelected}
                  onChange={onToggleSelectAll}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 cursor-pointer focus:ring-blue-500 focus:ring-offset-0"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Stage
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Centre
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Période
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Semaine
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Dates
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Places
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Prix
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Inscrits
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Attente
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sessions.map((session) => {
              const stage = stages.find(s => s.id === session.stage_id);
              const center = centers.find(c => c.id === session.center_id);
              const registrationCount = session.current_registrations || 0;
              const remainingPlaces = session.capacity - registrationCount;
              const isFull = remainingPlaces <= 0;
              const isArchived = session.archived === true;
              const isSelected = selectedIds.has(session.id);

              return (
                <tr
                  key={session.id}
                  className={clsx(
                    'transition-colors',
                    isSelected
                      ? 'bg-blue-50 hover:bg-blue-100'
                      : isArchived
                        ? 'bg-gray-50 hover:bg-gray-100'
                        : 'hover:bg-gray-50'
                  )}
                >
                  <td className="pl-4 pr-2 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggleSelect(session.id)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 cursor-pointer focus:ring-blue-500 focus:ring-offset-0"
                    />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div
                        className={clsx(
                          'text-sm font-medium max-w-[150px] truncate',
                          isArchived ? 'text-gray-400' : 'text-gray-900'
                        )}
                        title={stage?.title}
                      >
                        {stage?.title}
                      </div>
                      {isArchived && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 shrink-0">
                          <Archive className="h-3 w-3" />
                          Archivé
                          {session.archived_at && (
                            <span className="text-amber-600 font-normal">
                              · {formatArchivedAt(session.archived_at)}
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div
                      className={clsx(
                        'text-sm max-w-[120px] truncate',
                        isArchived ? 'text-gray-400' : 'text-gray-500'
                      )}
                      title={center?.name}
                    >
                      {center?.name}
                    </div>
                  </td>
                  <td className={clsx('px-4 py-3 whitespace-nowrap text-sm', isArchived ? 'text-gray-400' : 'text-gray-500')}>
                    {session.periode}
                  </td>
                  <td className={clsx('px-4 py-3 whitespace-nowrap text-sm', isArchived ? 'text-gray-400' : 'text-gray-500')}>
                    {session.semaine || '-'}
                  </td>
                  <td className={clsx('px-4 py-3 whitespace-nowrap text-sm', isArchived ? 'text-gray-400' : 'text-gray-500')}>
                    {formatDate(session.start_date)} - {formatDate(session.end_date)}
                  </td>
                  <td className={clsx('px-4 py-3 whitespace-nowrap text-sm', isArchived ? 'text-gray-400' : 'text-gray-500')}>
                    {session.capacity}
                  </td>
                  <td className={clsx('px-4 py-3 whitespace-nowrap text-sm', isArchived ? 'text-gray-400' : 'text-gray-500')}>
                    <div>
                      {session.prix_normal} €
                      {session.prix_reduit && (
                        <div className="text-xs text-gray-400">
                          Réduit: {session.prix_reduit} €
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <span className={clsx('font-medium', isFull && !isArchived ? 'text-red-600' : isArchived ? 'text-gray-400' : 'text-gray-700')}>
                      {registrationCount}
                    </span>
                    <span className={isArchived ? 'text-gray-400' : 'text-gray-500'}>/{session.capacity}</span>
                    {isFull && !isArchived && (
                      <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Complet
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {waitingListCounts[session.id] ? (
                      <button
                        onClick={() => onViewWaitingList(session.id)}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200"
                      >
                        <ListWait className="h-3 w-3 mr-1" />
                        {waitingListCounts[session.id]}
                      </button>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end items-center space-x-2">
                      {!isArchived ? (
                        <>
                          <button
                            onClick={() => handleOpenModal(session)}
                            className="text-primary-600 hover:text-primary-900"
                            title="Modifier"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDuplicate(session)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Dupliquer"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => onArchive(session.id)}
                            className="text-amber-600 hover:text-amber-800"
                            title="Archiver"
                          >
                            <Archive className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setDeleteId(session.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => onUnarchive(session.id)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 transition-colors"
                          title="Désarchiver"
                        >
                          <ArchiveRestore className="h-3.5 w-3.5" />
                          Désarchiver
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SessionTable;
